import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";

import { APP_API_V1_BASE } from "../../../../config/api";
const API_BASE = `${APP_API_V1_BASE}`;

const normalizeToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export default function BulkProvisionModal({
  open,
  onClose,
  onSuccess,
  assets,
  token,
  activeOrg,
}) {
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    device_type_id: "",
    name_prefix: "",
    protocol: "MQTT",
  });

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  useEffect(() => {
    if (!open) return;
    setError("");
    setResult(null);
    setSubmitting(false);
    setForm({
      device_type_id: "",
      name_prefix: "",
      protocol: "MQTT",
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    const loadTypes = async () => {
      try {
        setLoadingTypes(true);
        const res = await axios.get(`${API_BASE}/device-types`, {
          headers: authHeaders,
        });
        if (mounted) setDeviceTypes(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setDeviceTypes([]);
      } finally {
        if (mounted) setLoadingTypes(false);
      }
    };
    loadTypes();
    return () => {
      mounted = false;
    };
  }, [open, activeOrg?.id]);

  const compatibleDeviceTypes = useMemo(() => {
    if (!assets?.length) return deviceTypes;
    return deviceTypes.filter((type) => {
      const typeToken = normalizeToken(type.name);
      if (!typeToken) return true;
      return assets.every((asset) => {
        const assetTokens = [
          normalizeToken(asset.asset_type),
          normalizeToken(asset.model),
        ].filter(Boolean);
        if (!assetTokens.length) return true;
        return assetTokens.some(
          (token) => typeToken.includes(token) || token.includes(typeToken),
        );
      });
    });
  }, [assets, deviceTypes]);

  useEffect(() => {
    if (!open || !compatibleDeviceTypes.length) return;
    setForm((prev) =>
      prev.device_type_id
        ? prev
        : { ...prev, device_type_id: compatibleDeviceTypes[0].id },
    );
  }, [open, compatibleDeviceTypes]);

  const ineligibleAssets = useMemo(
    () => (assets || []).filter((asset) => asset.status !== "IN_STOCK"),
    [assets],
  );

  const handleSubmit = async () => {
    setError("");
    if (!assets?.length) {
      setError("Select at least one asset first.");
      return;
    }
    if (!form.device_type_id) {
      setError("Choose a device type.");
      return;
    }
    if (ineligibleAssets.length) {
      setError("Only in-stock assets can be bulk provisioned.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/devices/${activeOrg.id}/bulk-provision`,
        {
          asset_ids: assets.map((asset) => asset.id),
          device_type_id: form.device_type_id,
          name_prefix: form.name_prefix.trim() || undefined,
          protocol: form.protocol,
        },
        { headers: authHeaders },
      );
      setResult(res.data);
      onSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Bulk provisioning failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Bulk provision devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create logical devices from selected in-stock assets while
            preserving the existing single-asset wizard.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Alert severity={result.failed ? "warning" : "success"}>
              {result.message} — {result.succeeded} succeeded
              {result.failed ? `, ${result.failed} failed` : ""}.
            </Alert>
          )}
          {ineligibleAssets.length > 0 && (
            <Alert severity="warning">
              {ineligibleAssets.length} selected asset
              {ineligibleAssets.length !== 1 ? "s are" : " is"} not in stock and
              cannot be provisioned in bulk.
            </Alert>
          )}

          <Box display="flex" flexWrap="wrap" gap={1}>
            {(assets || []).slice(0, 8).map((asset) => (
              <Chip
                key={asset.id}
                label={`${asset.serial_number} · ${asset.asset_type || asset.model || "Asset"}`}
                variant="outlined"
              />
            ))}
            {(assets?.length || 0) > 8 && (
              <Chip label={`+${assets.length - 8} more`} variant="outlined" />
            )}
          </Box>

          <TextField
            select
            label="Device type"
            size="small"
            value={form.device_type_id}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, device_type_id: e.target.value }))
            }
            disabled={loadingTypes}
            helperText={
              compatibleDeviceTypes.length
                ? "Only device families compatible with the selected assets are shown."
                : "No compatible device type is visible for these assets."
            }
          >
            {compatibleDeviceTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
            gap={2}
          >
            <TextField
              label="Device name prefix"
              size="small"
              value={form.name_prefix}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name_prefix: e.target.value }))
              }
              helperText="Each asset serial will be appended automatically."
            />
            <TextField
              select
              label="Protocol"
              size="small"
              value={form.protocol}
              disabled
            >
              <MenuItem value="MQTT">MQTT</MenuItem>
            </TextField>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loadingTypes || !compatibleDeviceTypes.length}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DeviceHubIcon fontSize="small" />
            )
          }
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          {submitting ? "Provisioning..." : "Bulk provision"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
